// ./src/contexts/SignalRContext.tsx
import React, { createContext, useContext, useMemo } from "react";

import { useSignalR, SignalRMessage } from "@/hooks/useSignalR";

export interface SignalRContextValue {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  isReconnecting: boolean;

  // Connection operations
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  reconnect: () => Promise<void>;

  // Event subscription
  onDataUpdated: (callback: (message: SignalRMessage) => void) => () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;

  // Display info
  displayId: string | null;
  accountId: string | null;
}

const SignalRContext = createContext<SignalRContextValue | undefined>(
  undefined
);

export interface SignalRProviderProps {
  children: React.ReactNode;
  options?: {
    /** Auto-connect on mount */
    autoConnect?: boolean;
    /** Enable debug logging */
    debug?: boolean;
    /** Custom reconnection delay in milliseconds */
    reconnectDelay?: number;
    /** Min delay before fetching data after receiving update (milliseconds) */
    minUpdateDelay?: number;
    /** Max delay before fetching data after receiving update (milliseconds) */
    maxUpdateDelay?: number;
  };
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({
  children,
  options = {},
}) => {
  const {
    autoConnect = true,
    debug = false,
    reconnectDelay = 5000,
    minUpdateDelay = 0,
    maxUpdateDelay = 30000,
  } = options;

  const signalR = useSignalR({
    autoConnect,
    debug,
    reconnectDelay,
    minUpdateDelay,
    maxUpdateDelay,
  });

  const contextValue: SignalRContextValue = useMemo(
    () => ({
      connectionState: signalR.connectionState,
      isConnected: signalR.isConnected,
      isConnecting: signalR.isConnecting,
      isReconnecting: signalR.isReconnecting,
      connect: signalR.connect,
      disconnect: signalR.disconnect,
      reconnect: signalR.reconnect,
      onDataUpdated: signalR.onDataUpdated,
      lastError: signalR.lastError,
      clearError: signalR.clearError,
      displayId: signalR.displayId,
      accountId: signalR.accountId,
    }),
    [signalR]
  );

  return (
    <SignalRContext.Provider value={contextValue}>
      {children}
    </SignalRContext.Provider>
  );
};

// Custom hook for using the SignalR context
export const useSignalRContext = (): SignalRContextValue => {
  const context = useContext(SignalRContext);
  // if (context === undefined) {
  //   throw new Error("useSignalRContext must be used within a SignalRProvider");
  // }
  return (
    context ?? {
      isConnected: false,
      isConnecting: false,
      isReconnecting: false,

      // Connection operations
      connect: async () => {},
      disconnect: async () => {},
      reconnect: async () => {},

      // Event subscription
      onDataUpdated: (_: (message: SignalRMessage) => void) => () => {},

      // Error handling
      lastError: null,
      clearError: () => {},

      // Display info
      displayId: null,
      accountId: null,
    }
  );
};
